import { useState } from "react";
import { X, ChevronLeft, ChevronRight, Image as ImageIcon, Play } from "lucide-react";

/**
 * WeRoad-style hero mosaic: 1 large image on the left, 2x2 grid on the right.
 * Click anywhere to open a fullscreen lightbox carousel.
 *
 * `media` is the array from GET /trips/{id}/media — each item is { url, type, caption }.
 * `fallback` is the trip's main image_url, used when media is empty.
 */
const TripHeroGallery = ({ media = [], fallback }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const items = media.length > 0 ? media : (fallback ? [{ url: fallback, type: "photo" }] : []);
  if (items.length === 0) return null;

  // Mosaic layout: 1 big left + up to 4 small on the right (2x2)
  const leftItem = items[0];
  const rightItems = items.slice(1, 5);
  // Fill empty grid slots with the left image for visual balance
  while (rightItems.length < 4) rightItems.push(leftItem);

  const openLightbox = (idx) => { setActive(Math.min(idx, items.length - 1)); setOpen(true); };
  const next = () => setActive((a) => (a + 1) % items.length);
  const prev = () => setActive((a) => (a - 1 + items.length) % items.length);

  const renderTile = (item, idx, classes, withOverlay) => {
    if (!item) return null;
    const isVideo = item.type === "video";
    return (
      <button
        key={`${item.url}-${idx}`}
        type="button"
        onClick={() => openLightbox(idx)}
        className={`relative overflow-hidden bg-muted cursor-pointer group ${classes}`}
        data-testid={`trip-hero-tile-${idx}`}
      >
        {isVideo ? (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground/10">
            <video src={item.url} className="w-full h-full object-cover" muted playsInline />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="w-5 h-5 text-foreground ml-0.5" />
              </div>
            </div>
          </div>
        ) : (
          <img src={item.url} alt={item.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        )}
        {withOverlay && items.length > 5 && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-foreground text-xs font-medium">
              <ImageIcon className="w-3.5 h-3.5" /> Show all {items.length} photos
            </span>
          </div>
        )}
      </button>
    );
  };

  return (
    <>
      <div className="container mx-auto px-4 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 rounded-3xl overflow-hidden" data-testid="trip-hero-gallery">
          {/* Left: big tile (only on sm+ screens) */}
          <div className="hidden sm:block">
            {renderTile(leftItem, 0, "rounded-l-3xl h-[460px] w-full", false)}
          </div>
          {/* Right: 2x2 mini grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:h-[460px]">
            {renderTile(rightItems[0], 1, "rounded-tr-3xl sm:rounded-tr-3xl rounded-tl-3xl sm:rounded-tl-none h-[200px] sm:h-auto", false)}
            {renderTile(rightItems[1], 2, "h-[200px] sm:h-auto", false)}
            {renderTile(rightItems[2], 3, "h-[180px] sm:h-auto", false)}
            {renderTile(rightItems[3], 4, "rounded-br-3xl h-[180px] sm:h-auto", true)}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="trip-lightbox" onClick={() => setOpen(false)}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="max-w-5xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {items[active]?.type === "video" ? (
              <video src={items[active].url} controls autoPlay className="w-full max-h-[80vh] rounded-xl bg-black" />
            ) : (
              <img src={items[active]?.url} alt={items[active]?.caption || ""} className="w-full max-h-[80vh] object-contain rounded-xl" />
            )}
            {items[active]?.caption && (
              <p className="text-white/80 text-sm text-center mt-3">{items[active].caption}</p>
            )}
            <p className="text-white/50 text-xs text-center mt-1">{active + 1} / {items.length}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default TripHeroGallery;
